// Cold point form state ⇄ the POST/PUT /api/cold-chain/points body (spec §5, §9). Pure.
// Presets carry no keys here: the server owns the key lists (server/src/coldchain/presets.ts).
export const SOURCE_FAMILIES = ['eye', 'ble', 'wired', 'custom'];
export const RANGE_PRESETS = [
  { id: 'chilled', min: 2, max: 8 },
  { id: 'cool', min: 0, max: 4 },
  { id: 'frozen', min: null, max: -18 },
  { id: 'custom' },
];

// Mirrors server/src/coldchain/validate.ts: ≤ 8 keys per list, key spec = [!]name[*factor], "!" on door keys only.
const KEY_SPEC_RE = /^(!)?([A-Za-z][A-Za-z0-9_]{0,63})(?:\*(-?\d+(?:\.\d+)?))?$/;
const MAX_KEYS = 8;

export function emptyForm() {
  return {
    name: '', locationDetail: '', family: 'eye', sensorSlot: 1,
    customTempKeys: '', customScale: '1', customHumidityKeys: '', customHumidityScale: '1',
    customDoorKeys: '', doorMagnet: false, customBatteryKeys: '', customBatteryScale: '1', tempOffset: '0',
    rangePreset: 'chilled', rangeMin: '2', rangeMax: '8', alarmDelayMinutes: '5', noDataMinutes: '10', enabled: true,
    calibrationReference: '', calibratedOn: '', calibrationDueOn: '', reason: '',
  };
}

export function rangePresetOf(min, max) {
  const hit = RANGE_PRESETS.find((r) => r.id !== 'custom' && r.min === (min ?? null) && r.max === (max ?? null));
  return hit ? hit.id : 'custom';
}

const str = (v) => (v === null || v === undefined ? '' : String(v));
const dateStr = (v) => (v ? String(v).slice(0, 10) : '');
const join = (list) => (list ?? []).join(', ');

export function fromPoint(p) {
  const m = /^(eye|ble|wired):([1-4])$/.exec(p.tempSource ?? '');
  return {
    name: p.name ?? '',
    locationDetail: p.locationDetail ?? '',
    family: m ? m[1] : 'custom',
    sensorSlot: m ? Number(m[2]) : 1,
    customTempKeys: m ? '' : join(p.tempKeys),
    customScale: str(p.tempScale ?? 1),
    customHumidityKeys: m ? '' : join(p.humidityKeys),
    customHumidityScale: str(p.humidityScale ?? 1),
    customDoorKeys: m ? '' : join(p.doorKeys),
    // Stored as door keys being present (no separate column); only an EYE sensor has the opt-in.
    doorMagnet: !!m && m[1] === 'eye' && (p.doorKeys ?? []).length > 0,
    customBatteryKeys: m ? '' : join(p.batteryKeys),
    customBatteryScale: str(p.batteryScale ?? 1),
    tempOffset: str(p.tempOffset ?? 0),
    rangePreset: rangePresetOf(p.rangeMin, p.rangeMax),
    rangeMin: str(p.rangeMin),
    rangeMax: str(p.rangeMax),
    alarmDelayMinutes: str(p.alarmDelayMinutes ?? 5),
    noDataMinutes: str(p.noDataMinutes ?? 10),
    enabled: p.enabled !== false,
    calibrationReference: p.calibrationReference ?? '',
    calibratedOn: dateStr(p.calibratedOn),
    calibrationDueOn: dateStr(p.calibrationDueOn),
    reason: '',
  };
}

export function applyRangePreset(form, id) {
  const r = RANGE_PRESETS.find((x) => x.id === id);
  if (!r || id === 'custom') return { ...form, rangePreset: 'custom' };
  return { ...form, rangePreset: id, rangeMin: str(r.min), rangeMax: str(r.max) };
}

export function splitKeys(s) {
  return String(s ?? '').split(',').map((x) => x.trim()).filter(Boolean);
}

const num = (s) => (String(s).trim() === '' ? null : Number(s));
const isWhole = (s) => /^\d+$/.test(String(s).trim());
const validScale = (s) => Number.isFinite(Number(s)) && String(s).trim() !== '' && Number(s) !== 0;

/** 'invalid' | 'inverted' | null for one key list. */
function keyListError(raw, allowInverted) {
  const keys = splitKeys(raw);
  if (keys.length > MAX_KEYS) return 'invalid';
  for (const k of keys) {
    const m = KEY_SPEC_RE.exec(k);
    if (!m) return 'invalid';
    if (m[1] && !allowInverted) return 'inverted';
  }
  return null;
}

// Mirrors the server's calibration-class list (on the PAYLOAD shape): changing any of these, or switching
// monitoring off, needs a reason for the change. Arrays compare element-wise, order-sensitive.
export const CALIBRATION_FIELDS = ['tempOffset', 'rangeMin', 'rangeMax', 'tempSource', 'tempKeys', 'tempScale', 'alarmDelayMinutes', 'noDataMinutes'];

const sameValue = (a, b) => {
  if (Array.isArray(a) || Array.isArray(b)) {
    return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => v === b[i]);
  }
  return (a ?? null) === (b ?? null);
};

export function reasonRequiredFor(initialPayload, nextPayload) {
  if (!initialPayload || !nextPayload) return [];
  const names = CALIBRATION_FIELDS.filter((f) => !sameValue(initialPayload[f], nextPayload[f]));
  if (initialPayload.enabled === true && nextPayload.enabled === false) names.push('enabled');
  return names;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function validateForm(form, initialPayload = null) {
  const errors = {};
  if (!form.name.trim()) errors.name = 'required';
  else if (form.name.trim().length > 60) errors.name = 'length';
  if (form.locationDetail.trim().length > 120) errors.locationDetail = 'length';
  const min = num(form.rangeMin);
  const max = num(form.rangeMax);
  if ((min !== null && !Number.isFinite(min)) || (max !== null && !Number.isFinite(max))) errors.range = 'number';
  else if (min === null && max === null) errors.range = 'required';
  else if (min !== null && max !== null && min >= max) errors.range = 'order';
  if (!isWhole(form.alarmDelayMinutes) || Number(form.alarmDelayMinutes) > 1440) errors.alarmDelayMinutes = 'integer';
  if (!isWhole(form.noDataMinutes) || Number(form.noDataMinutes) < 2 || Number(form.noDataMinutes) > 1440) errors.noDataMinutes = 'integer';
  if (!Number.isFinite(Number(form.tempOffset))) errors.tempOffset = 'number';
  if (form.family === 'custom') {
    if (splitKeys(form.customTempKeys).length === 0) errors.customTempKeys = 'required';
    else { const e = keyListError(form.customTempKeys, false); if (e) errors.customTempKeys = e; }
    for (const [field, raw, inv] of [
      ['customHumidityKeys', form.customHumidityKeys, false],
      ['customDoorKeys', form.customDoorKeys, true],
      ['customBatteryKeys', form.customBatteryKeys, false],
    ]) {
      const e = keyListError(raw, inv);
      if (e) errors[field] = e;
    }
    for (const [field, raw] of [
      ['customScale', form.customScale], ['customHumidityScale', form.customHumidityScale], ['customBatteryScale', form.customBatteryScale],
    ]) {
      if (!validScale(raw)) errors[field] = 'number';
    }
  }
  if (form.calibrationReference.trim().length > 200) errors.calibrationReference = 'length';
  for (const f of ['calibratedOn', 'calibrationDueOn']) {
    if (form[f] !== '' && !DATE_RE.test(form[f])) errors[f] = 'date';
  }
  if (initialPayload && !errors.reason && reasonRequiredFor(initialPayload, toPayload(form)).length > 0 && form.reason.trim() === '') {
    errors.reason = 'required';
  }
  return errors;
}

export function toPayload(form, initial = null) {
  const base = {
    name: form.name.trim(),
    locationDetail: form.locationDetail.trim() || null,
    tempOffset: Number(form.tempOffset) || 0,
    rangeMin: num(form.rangeMin),
    rangeMax: num(form.rangeMax),
    alarmDelayMinutes: Number(form.alarmDelayMinutes),
    noDataMinutes: Number(form.noDataMinutes),
    enabled: form.enabled,
    calibrationReference: form.calibrationReference.trim() || null,
    calibratedOn: form.calibratedOn || null,
    calibrationDueOn: form.calibrationDueOn || null,
  };
  if (form.reason.trim()) base.reason = form.reason.trim();
  if (typeof initial?.versionNo === 'number') base.expectedVersionNo = initial.versionNo;
  if (form.family === 'custom') {
    return {
      ...base,
      tempSource: 'custom',
      tempKeys: splitKeys(form.customTempKeys),
      tempScale: Number(form.customScale) || 1,
      humidityKeys: splitKeys(form.customHumidityKeys),
      humidityScale: Number(form.customHumidityScale) || 1,
      doorKeys: splitKeys(form.customDoorKeys),
      batteryKeys: splitKeys(form.customBatteryKeys),
      batteryScale: Number(form.customBatteryScale) || 1,
    };
  }
  const preset = { ...base, tempSource: `${form.family}:${form.sensorSlot}` };
  // The server maps the magnet key only when this is true (an EYE with no magnet reads "open" forever).
  return form.family === 'eye' ? { ...preset, doorMagnet: !!form.doorMagnet } : preset;
}
